import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = {
  title: '', customer_id: '', lead_id: '', stage: 'prospecting',
  value: '', probability: '', expected_close: '', assigned_to: '', notes: '',
};
const LIMIT = 15;
const STAGES = ['prospecting', 'qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
const stageColor = {
  prospecting: 'info', qualification: 'primary', proposal: 'warning',
  negotiation: 'warning', closed_won: 'active', closed_lost: 'inactive',
};
const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function Opportunities({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [pipeline, setPipeline] = useState([]);

  useEffect(() => { setTitle('Opportunities'); }, [setTitle]);
  useEffect(() => {
    api.get('/customers/all').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/leads/all').then(r => setLeads(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/opportunities/pipeline').then(r => setPipeline(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: LIMIT });
      if (filterStage) params.set('stage', filterStage);
      const { data } = await api.get(`/opportunities?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterStage]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({
      title: item.title, customer_id: item.customer_id || '',
      lead_id: item.lead_id || '', stage: item.stage, value: item.value,
      probability: item.probability, expected_close: item.expected_close ? item.expected_close.split('T')[0] : '',
      assigned_to: item.assigned_to || '', notes: item.notes || '',
    });
    setEditId(item.id); setModal(true);
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/opportunities/${editId}`, form);
      else        await api.post('/opportunities', form);
      closeModal(); load();
      api.get('/opportunities/pipeline').then(r => setPipeline(r.data)).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving opportunity');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete opportunity "${title}"?`)) return;
    try {
      await api.delete(`/opportunities/${id}`); load();
      api.get('/opportunities/pipeline').then(r => setPipeline(r.data)).catch(() => {});
    } catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const totalPipelineValue = pipeline.reduce((s, p) => s + parseFloat(p.total_value || 0), 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Opportunities</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Opportunity
        </button>
      </div>

      {/* Pipeline Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {STAGES.map(stage => {
          const s = pipeline.find(p => p.stage === stage) || { count: 0, total_value: 0 };
          return (
            <div key={stage} className="card" style={{ flex: '1 1 140px', padding: '12px 16px', minWidth: 130 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 4 }}>
                {stage.replace('_', ' ')}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{s.count}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>${fmt(s.total_value)}</div>
            </div>
          );
        })}
        <div className="card" style={{ flex: '1 1 140px', padding: '12px 16px', minWidth: 130, background: 'var(--primary)', color: '#fff' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.8, marginBottom: 4 }}>Total Pipeline</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>${fmt(totalPipelineValue)}</div>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search title or customer…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 170 }} value={filterStage} onChange={e => { setFilterStage(e.target.value); setPage(1); }}>
            <option value="">All Stages</option>
            {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} opportunities</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Title</th><th>Customer</th><th>Stage</th>
                  <th>Value</th><th>Probability</th><th>Expected Close</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No opportunities found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.title}</strong></td>
                    <td>{item.customer_name || item.lead_name || '—'}</td>
                    <td>
                      <span className={`badge badge-${stageColor[item.stage] || 'info'}`} style={{ textTransform: 'capitalize' }}>
                        {item.stage.replace('_', ' ')}
                      </span>
                    </td>
                    <td><strong>${fmt(item.value)}</strong></td>
                    <td>{item.probability}%</td>
                    <td>{item.expected_close ? item.expected_close.split('T')[0] : '—'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.title)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Opportunity' : 'Add Opportunity'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Title *</label>
                  <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Opportunity title" />
                </div>
                <div className="form-group">
                  <label>Customer</label>
                  <select className="form-control" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
                    <option value="">— None —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Lead</label>
                  <select className="form-control" value={form.lead_id} onChange={e => f('lead_id', e.target.value)}>
                    <option value="">— None —</option>
                    {leads.map(l => <option key={l.id} value={l.id}>{l.name}{l.company ? ` (${l.company})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stage</label>
                  <select className="form-control" value={form.stage} onChange={e => f('stage', e.target.value)}>
                    {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Value ($)</label>
                  <input type="number" className="form-control" value={form.value} onChange={e => f('value', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label>Probability (%)</label>
                  <input type="number" className="form-control" value={form.probability} onChange={e => f('probability', e.target.value)} placeholder="0" min="0" max="100" />
                </div>
                <div className="form-group">
                  <label>Expected Close Date</label>
                  <input type="date" className="form-control" value={form.expected_close} onChange={e => f('expected_close', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Additional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
